import React, { ChangeEvent, FocusEvent, useEffect, useState } from 'react';
import styles from './Input.module.scss';
import { AlertCircleIcon, PreviewOffIcon, PreviewOnIcon } from '../../Icons';

export interface InputProps extends React.HTMLProps<HTMLInputElement & HTMLTextAreaElement> {
	label?: string;
	validators?: Array<{
		validate: (value: string, ...rest: any[]) => boolean;
		errorMessage: string;
	}>;
	valueListener: (name: string, value: string, isValid: boolean) => void;
	name: string;
	inputSize?: 'default' | 'big' | 'multiline';
	enablePreview?: boolean;
	withAdornment?: 'left' | 'right';
	hideErrorMessage?: boolean;
	errorMessage?: string | null;
	error?: boolean;
	emitErrorMessage?: (message: string | null) => void;
	clearErrorMessage?: () => void;
	fillParent?: boolean;
	roundCorners?: boolean;
	centerText?: boolean;
	justInput?: boolean;
	toUpperCase?: boolean;
	multiline?: boolean;
}

export const Input = React.forwardRef((props: InputProps, ref) => {
	const {
		label,
		validators = [],
		valueListener,
		type = 'text',
		enablePreview = false,
		withAdornment = false,
		name,
		hideErrorMessage,
		errorMessage,
		error = false,
		fillParent = false,
		roundCorners = false,
		centerText = false,
		emitErrorMessage,
		clearErrorMessage,
		justInput = false,
		inputSize = 'default',
		className,
		toUpperCase = false,
		multiline,
		...rest
	} = props;

	const [value, setValue] = useState<string>((rest.value || '') as string);
	const [_errorMessage, setErrorMessage] = useState('');
	const [isValid, setIsValid] = useState(!rest.required && rest.value === '');
	const [shouldShowError, setShouldShowError] = useState(false);
	const [_type, setType] = useState(type);

	useEffect(() => {
		if (emitErrorMessage) {
			emitErrorMessage(!isValid ? _errorMessage : null);
		}
	}, [isValid, _errorMessage, emitErrorMessage]);

	const validate = (inputValue: string): boolean => {
		let valid = true;
		if (!rest.required && value === '') return true;
		if (rest.readOnly) return true;

		for (let i = 0; i < validators.length; i++) {
			const validator = validators[i];
			valid = validator.validate(inputValue);

			if (!valid) {
				setErrorMessage(validator.errorMessage);
				break;
			}
		}

		return valid;
	};

	useEffect(() => {
		if (rest.value) {
			const result = validate((rest.value || '') as string);
			setIsValid(result);
			setShouldShowError(!result);
		}
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		if (value || !rest.required) {
			setIsValid(validate(value));
		}
		// eslint-disable-next-line
	}, [validators]);

	const toggleInputPreview = () => setType((t) => (t === 'text' ? type : 'text'));

	useEffect(() => {
		if (rest.value) {
			setValue(rest.value as string);
			setIsValid(validate(rest.value as string));
		} else if (value !== '') {
			setValue(rest.value as string);
			setIsValid(validate(rest.value as string));
			setShouldShowError(false);
		}
		// eslint-disable-next-line
	}, [rest.value]);

	const onChange = (e: ChangeEvent<HTMLInputElement & HTMLTextAreaElement>) => {
		setValue(e.target.value);
		setIsValid(validate(e.target.value));
		clearErrorMessage && clearErrorMessage();
	};

	const onBlur = (e: FocusEvent<HTMLInputElement & HTMLTextAreaElement>) => {
		setIsValid(validate(e.target.value));
		setShouldShowError(true);

		if (typeof rest.onBlur === 'function') {
			rest.onBlur(e);
		}
	};

	// eslint-disable-next-line
	useEffect(() => {
		if (!rest.readOnly) valueListener(name, value, isValid);
	}, [value, isValid]);

	const classNames = [
		multiline ? styles.inputMultiline : styles.input,
		enablePreview ? styles.preview : '',
		withAdornment ? styles[withAdornment] : '',
		fillParent ? styles.fillParent : '',
		roundCorners ? styles.roundCorners : '',
		centerText ? styles.centerText : '',
		styles.size !== 'default' ? styles[inputSize] : '',
		className,
		multiline ? styles.textarea : '',
	].join(' ');

	if (justInput) {
		return (
			<section className={styles.container}>
				{multiline ? (
					<textarea
						ref={ref as any}
						className={classNames}
						{...rest}
						name={name}
						value={value}
						onChange={onChange}
						onBlur={onBlur}
						rows={2}
						max-rows={2}
					/>
				) : (
					<input
						ref={ref as any}
						className={classNames}
						{...rest}
						name={name}
						value={value}
						type={enablePreview ? _type : type}
						onChange={onChange}
						onBlur={onBlur}
					/>
				)}
			</section>
		);
	}

	return (
		<section
			className={[
				styles.container,
				(!isValid && shouldShowError) || errorMessage || error ? styles.error : '',
			].join(' ')}
		>
			<div className={[styles.error, (!isValid && shouldShowError) || errorMessage ? styles.show : ''].join(' ')}>
				{!hideErrorMessage && (
					<>
						<AlertCircleIcon className={styles.icon} />
						<em className={styles.errorMessage}>{errorMessage || _errorMessage}</em>
					</>
				)}
			</div>
			<input
				ref={ref as any}
				className={classNames}
				{...rest}
				name={name}
				value={value}
				type={enablePreview ? _type : type}
				onChange={onChange}
				onBlur={onBlur}
			/>
			{label && (
				<label className={styles.label} htmlFor={name}>
					{label}
				</label>
			)}
			{enablePreview && (
				<div className={styles.previewIcon} onClick={toggleInputPreview}>
					{_type === type ? <PreviewOffIcon /> : <PreviewOnIcon />}
				</div>
			)}
		</section>
	);
});
